import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Table, Image, Typography, Button, Popconfirm } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

import { semiFinishedActions } from "../../../store/slices/semiFinished-slice";
import { SemiFinishedUpdateModal } from "../semiFenishedSectionModal/SemiFinishedUpdateModal/SemiFinishedUpdateModal";
import { semiFinishedService } from "../../../services/semiFinished-services";
import { toast } from "react-toastify";

const { Text } = Typography;
const DEFAULT_IMAGE = "/images/photoForGoFit.jpg";

const SemiFinishedListComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const {
    semiFinishedArray,
    semiFinishedFiltered,
    searchActive,
    trigger,
    status,
  } = useSelector((store) => store.semiFinished);
  const [selectedForUpdate, setSelectedForUpdate] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});

  useEffect(() => {
    dispatch(semiFinishedActions.getAllSemiFinished());
  }, [trigger]);

  const handleDeleteConfirm = async (id) => {
    try {
      setLoadingStates((prev) => ({ ...prev, [id]: true }));
      await semiFinishedService.deleteSemiFinishById(id);
      dispatch(semiFinishedActions.changeTrigger());
      toast.success(t("Semi-finished deleted!"));
    } catch (error) {
      console.error("Error deleting semi-finished:", error);
      toast.error(t("Failed to delete the semi-finished"));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  const semiFinishedToRender = searchActive
    ? semiFinishedFiltered
    : semiFinishedArray;
  const currentLanguage = i18n.language;

  const columns = [
    {
      title: t("Image"),
      key: "img",
      width: "12%",
      render: (_, record) => (
        <Image
          src={record.img || DEFAULT_IMAGE}
          alt={
            record.name?.[currentLanguage] || record.name?.en || "Food Image"
          }
          width={56}
          height={56}
          style={{ objectFit: "cover", borderRadius: 6 }}
          fallback={DEFAULT_IMAGE}
          preview={false}
        />
      ),
    },
    {
      title: t("modal-name-input-new-category"),
      key: "name",
      width: "22%",
      render: (_, record) => (
        <Text>{record.name?.[currentLanguage] || record.name?.en}</Text>
      ),
    },
    {
      title: t("Weight"),
      dataIndex: "weight",
      key: "weight",
      width: "10%",
    },
    {
      title: t("weight-netto"),
      dataIndex: "weightNetto",
      key: "weightNetto",
      width: "10%",
      render: (v) => v || 0,
    },
    {
      title: t("modal-price"),
      dataIndex: "price",
      key: "price",
      width: "10%",
    },
    {
      title: t("Cost"),
      dataIndex: "cost",
      key: "cost",
      width: "12%",
    },
    {
      title: t("Category"),
      key: "category",
      width: "14%",
      render: (_, record) =>
        record.category?.name?.[currentLanguage] ||
        record.category?.name?.en ||
        t("unknown-category"),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: "10%",
      align: "right",
      render: (_, record) => (
        // add edit button
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setSelectedForUpdate(record)}
          />
          <Popconfirm
            title={t("Are you sure you want to delete this item?")}
            onConfirm={() => handleDeleteConfirm(record.id)}
            okText={t("Yes")}
            cancelText={t("No")}
            okButtonProps={{ danger: true }}
          >
            <Button
              type="dashed"
              danger
              icon={<DeleteOutlined />}
              loading={loadingStates[record.id]}
              disabled={loadingStates[record.id]}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={semiFinishedToRender}
        columns={columns}
        rowKey="id"
        loading={status === "Loading"}
        scroll={{ y: "calc(83vh - 120px)" }}
        pagination={false}
        locale={{
          emptyText: searchActive
            ? t("No semi-finished products found matching your search")
            : t("No semi-finished products available"),
        }}
      />

      {selectedForUpdate &&
        createPortal(
          <SemiFinishedUpdateModal
            setIsOpenUpdate={() => setSelectedForUpdate(null)}
            semiFinished={selectedForUpdate}
          />,
          document.body,
        )}
    </>
  );
};

export default React.memo(SemiFinishedListComponent);
